import type { ChatTransport, UIMessage, UIMessageChunk } from "ai"

import { getSocket, nextRequestId } from "./connection"

/**
 * The AI SDK's chat transport, carried by Socket.IO instead of an HTTP stream.
 *
 * `useChat` only ever asks for a `ReadableStream<UIMessageChunk>`, so the SDK's
 * whole client side works unchanged — the chunks are identical, only the pipe
 * differs. That matters because the customer's gateway buffers HTTP response
 * bodies until they are complete: the answer arrived, but never word by word.
 *
 * Deliberately no reconnect-to-stream: a chat answer takes seconds, and resuming
 * one would need server-side buffering we do not have. On a lost connection the
 * turn is repeated, which is what the advisor expects anyway.
 */
export class SocketChatTransport<UI_MESSAGE extends UIMessage>
  implements ChatTransport<UI_MESSAGE>
{
  constructor(
    private readonly options: {
      /** Which lane on the server, e.g. "chat" for the coach chat. */
      lane?: string
      /** Extra fields for the request body, read fresh on every send. */
      body?: () => Record<string, unknown> | undefined
    } = {},
  ) {}

  sendMessages({
    messages,
    abortSignal,
  }: Parameters<ChatTransport<UI_MESSAGE>["sendMessages"]>[0]): Promise<
    ReadableStream<UIMessageChunk>
  > {
    const lane = this.options.lane ?? "chat"
    const socket = getSocket()
    const requestId = nextRequestId()

    const body = {
      ...this.options.body?.(),
      messages: messages.map((message) => ({
        role: message.role,
        content: message.parts
          .filter((part) => part.type === "text")
          .map((part) => (part as { text: string }).text)
          .join(""),
      })),
    }

    const stream = new ReadableStream<UIMessageChunk>({
      start(controller) {
        let closed = false
        const cleanup = () => {
          socket.off(`${lane}:chunk`, onChunk)
          socket.off(`${lane}:finish`, onFinish)
          socket.off(`${lane}:error`, onError)
          socket.off("disconnect", onDisconnect)
        }
        const close = () => {
          if (closed) return
          closed = true
          cleanup()
          controller.close()
        }
        const fail = (message: string) => {
          if (closed) return
          closed = true
          cleanup()
          controller.error(new Error(message))
        }

        // Every lane event carries the request id, because one connection serves
        // several streams at once.
        const onChunk = (payload: { requestId: string; chunk: UIMessageChunk }) => {
          if (payload.requestId !== requestId || closed) return
          controller.enqueue(payload.chunk)
        }
        const onFinish = (payload: { requestId: string }) => {
          if (payload.requestId === requestId) close()
        }
        const onError = (payload: { requestId: string; message?: string }) => {
          if (payload.requestId !== requestId) return
          fail(payload.message ?? "Die Antwort wurde abgebrochen.")
        }
        const onDisconnect = () => fail("Die Verbindung wurde unterbrochen.")

        socket.on(`${lane}:chunk`, onChunk)
        socket.on(`${lane}:finish`, onFinish)
        socket.on(`${lane}:error`, onError)
        socket.on("disconnect", onDisconnect)

        abortSignal?.addEventListener("abort", () => {
          socket.emit(`${lane}:stop`, { requestId })
          close()
        })

        socket.emit(`${lane}:start`, { requestId, body })
      },
    })

    return Promise.resolve(stream)
  }

  reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
    return Promise.resolve(null)
  }
}
